import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type EvidenceVerb as EvidenceVerbEnum,
  verbToAnalyticsVerb,
} from '@/models/analytics/features/evidence';
import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { EvidenceVerbChips } from './evidence-verb-chips';
import { EvidenceVerbPopover } from './evidence-verb-popover';
import type {
  EvidenceVerbCardId,
  EvidenceVerbSuggestionsProps,
} from './evidence-verb-suggestions.types';
import { useEvidenceHomepageVerbs } from './hooks/useEvidenceHomepageVerbs';
import { track } from '@/lib/analytics/track';

type SuggestionMode =
  | { state: 'chips' }
  | { state: 'popover'; activeVerbId: EvidenceVerbCardId };

/** Breathing room kept between the popover's last row and the viewport floor. */
const POPOVER_VIEWPORT_MARGIN = 24;

const researchCardMessages = defineMessages({
  heading: {
    defaultMessage: 'Look up',
    id: 'S2R+kT9NjQ',
    description: 'Heading of the Agent Home Evidence research starters card',
  },
  exitEvidence: {
    defaultMessage: 'Turn off Evidence only',
    id: 'pbVFulro61',
    description:
      'Accessible label for the research card control that returns the composer to Auto',
  },
});

const trackEvidenceVerbClicked = (
  verbId: EvidenceVerbCardId,
  isExpanded: boolean
) => {
  const analyticsVerb = verbToAnalyticsVerb[verbId];
  if (!analyticsVerb) return;
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceVerbClicked,
    props: { verb: analyticsVerb, is_expanded: isExpanded },
  });
};

/**
 * The desktop popover overlays the space beneath the composer, and that space is
 * finite. Measuring it is what lets the page stop RESERVING it: a reserve tall
 * enough for the longest verb list grew the column as the popover opened, which
 * shoved the vertically centred composer upwards and dropped it back on close.
 *
 * The anchor is measured, not the popover, because the popover carries an
 * entrance transform — reading its own rect would cap it against a position it
 * is still animating out of.
 */
const useViewportCappedPopover = (isOpen: boolean, isMobile: boolean) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!isOpen || isMobile) {
      setMaxHeight(undefined);
      return;
    }

    let frame = 0;
    const measure = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const available = Math.max(
        window.innerHeight -
          anchor.getBoundingClientRect().top -
          POPOVER_VIEWPORT_MARGIN,
        0
      );
      setMaxHeight((current) => (current === available ? current : available));
    };

    const scheduleMeasure = () => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    measure();
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      window.removeEventListener('resize', scheduleMeasure);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [isOpen, isMobile]);

  return { anchorRef, maxHeight };
};

export const EvidenceVerbSuggestions = ({
  onPopulateInput,
  onSuggestionHover,
  onExpandedChange,
  onFillVerbClick,
  onExitEvidence,
  initialVerbId,
  isMobile = false,
  layout = 'chips',
  isWorkHomeDirectionCTaxonomyEnabled = false,
}: EvidenceVerbSuggestionsProps) => {
  const intl = useIntl();
  const allVerbs = useEvidenceHomepageVerbs(
    isWorkHomeDirectionCTaxonomyEnabled
  );
  const verbs = useMemo(
    () => onFillVerbClick ? allVerbs : allVerbs.filter((v) => v.id !== 'fill'),
    [allVerbs, onFillVerbClick]
  );
  const isSuggestedWork = layout === 'suggestedWork';
  const [mode, setMode] = useState<SuggestionMode>(() =>
    isSuggestedWork
      ? {
          state: 'popover',
          activeVerbId: verbs.find((v) => v.id === initialVerbId)?.id ?? 'lookUp',
        }
      : { state: 'chips' }
  );
  const popoverRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);

  const guardedSuggestionHover = useCallback(
    (preview: string | null) => {
      if (isClosingRef.current) return;
      onSuggestionHover?.(preview);
    },
    [onSuggestionHover]
  );

  const handleExitComplete = useCallback(() => {
    onSuggestionHover?.(null);
    isClosingRef.current = false;
  }, [onSuggestionHover]);

  const handleVerbClick = useCallback(
    (verbId: EvidenceVerbCardId) => {
      isClosingRef.current = false;
      trackEvidenceVerbClicked(verbId, true);

      if (verbId === 'fill' && onFillVerbClick) {
        onFillVerbClick();
        return;
      }

      setMode({ state: 'popover', activeVerbId: verbId });
      onExpandedChange?.(true);
    },
    [onExpandedChange, onFillVerbClick]
  );

  const handleClose = useCallback(() => {
    isClosingRef.current = true;
    if (mode.state === 'popover') {
      trackEvidenceVerbClicked(mode.activeVerbId, false);
    }
    onSuggestionHover?.(null);
    setMode({ state: 'chips' });
    onExpandedChange?.(false);
  }, [mode, onSuggestionHover, onExpandedChange]);

  const handleItemClick = useCallback(
    (starter: string, verbId: EvidenceVerbCardId, triggerSuffix?: '@' | '#') => {
      isClosingRef.current = true;
      const analyticsVerb = verbToAnalyticsVerb[verbId] as EvidenceVerbEnum;
      if (analyticsVerb) {
        track({ options: { amplitude: false },
          event_name: TrackingEventName.EvidenceSuggestionClicked,
          props: { category: analyticsVerb, question: starter },
        });
      }
      onSuggestionHover?.(null);
      onPopulateInput(starter + (triggerSuffix ?? ''));
      // Inside the card one L1 is always open: the pick fills the composer and
      // the page takes the card away, so there is no list to fold.
      if (isSuggestedWork) return;
      setMode({ state: 'chips' });
      onExpandedChange?.(false);
    },
    [isSuggestedWork, onPopulateInput, onSuggestionHover, onExpandedChange]
  );

  const handleExitEvidence = useCallback(() => {
    isClosingRef.current = true;
    onSuggestionHover?.(null);
    onExitEvidence?.();
  }, [onExitEvidence, onSuggestionHover]);

  // The Agent Home card is page content with a list always open, so neither an
  // outside click nor Escape has anything to dismiss.
  useEffect(() => {
    if (mode.state !== 'popover' || isSuggestedWork) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, isSuggestedWork, mode.state]);

  const activeVerb =
    mode.state === 'popover'
      ? verbs.find((v) => v.id === mode.activeVerbId)
      : null;

  const isPopoverOpen = mode.state === 'popover';
  const { anchorRef, maxHeight: popoverMaxHeight } =
    useViewportCappedPopover(isPopoverOpen && !isSuggestedWork, isMobile);
  const verbList = (
    <EvidenceVerbChips
      verbs={verbs}
      onVerbClick={handleVerbClick}
      isMobile={isMobile && !isSuggestedWork}
      selectedId={isSuggestedWork ? activeVerb?.id : undefined}
      // Content inside a left-aligned card sits on the container's edge with
      // everything else in it. Only the free-standing collapsed row centres.
      align={isSuggestedWork ? 'start' : 'center'}
      // The Agent Home card holds its chips on one scrolling row; the landing's
      // chip row keeps wrapping, so Evidence is not recut by a Work ruling.
      overflow={isSuggestedWork ? 'scroll' : 'wrap'}
    />
  );

  if (isSuggestedWork) {
    const headingId = 'evidence-research-card-heading';
    // The section carries no border, background or shadow of its own: on
    // Agent Home this card is the open state of the Look up activity tile,
    // and the chrome that morphs between the two is a `layoutId` layer the
    // scene renders behind this content.
    return (
      <section
        className="w-full min-w-0 max-w-full px-5 pb-5 pt-4"
        data-testid="work-home-evidence-starters-card"
        data-compact-composer-interaction
        aria-labelledby={headingId}
      >
        <div className="flex min-h-7 items-center justify-between gap-3">
          {/* A <p>, not a heading: newBrand forces Exposure onto h1–h3. */}
          <TypographyP2
            id={headingId}
            className="min-w-0 truncate leading-5 tracking-normal text-text-primary"
          >
            {intl.formatMessage(researchCardMessages.heading)}
          </TypographyP2>
          {/* A bare x: the composer directly above already wears the Evidence
              only chip, so naming the mode again here is redundant rather
              than clarifying. The BEHAVIOUR is still leaving the mode. */}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            iconOnly
            onClick={handleExitEvidence}
            aria-label={intl.formatMessage(researchCardMessages.exitEvidence)}
            className="shrink-0 rounded-full text-text-secondary"
          >
            <X aria-hidden />
          </Button>
        </div>

        <div className="mt-3">{verbList}</div>

        {activeVerb ? (
          <div ref={popoverRef} className="mt-2 min-w-0">
            <EvidenceVerbPopover
              verbId={activeVerb.id}
              verbLabel={activeVerb.label}
              verbIcon={activeVerb.icon}
              items={activeVerb.items}
              onItemClick={(starter, triggerSuffix) =>
                handleItemClick(starter, activeVerb.id, triggerSuffix)
              }
              onItemHover={guardedSuggestionHover}
              onClose={handleClose}
              chrome="items"
            />
          </div>
        ) : null}
      </section>
    );
  }

  if (isMobile) {
    // Mobile: chips stay mounted to preserve layout height (prevents welcome text jump).
    // Popover overlays from the top of the container, scrollable on short viewports.
    return (
      <div className="relative flex w-full flex-col items-start pl-1">
        {/* Chips: always mounted for stable height; hidden when popover is open.
            duration-300 gives a gradual fade-out so the popover entrance doesn't feel like a hard switch. */}
        <div
          className={cn(
            'w-full transition-opacity duration-300 ease-out',
            isPopoverOpen && 'pointer-events-none opacity-0'
          )}
        >
          {verbList}
        </div>
        {/* Popover anchored at bottom, expands upward — scrollable for small devices.
            100ms delay lets the chips mostly fade before the popover appears. */}
        <AnimatePresence onExitComplete={handleExitComplete}>
          {isPopoverOpen && activeVerb && (
            <m.div
              ref={popoverRef}
              key={`popover-${activeVerb.id}`}
              className="absolute inset-x-0 bottom-0 z-10 max-h-[45dvh] overflow-y-auto"
              initial={{ opacity: 0, y: 6 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: { duration: 0.25, delay: 0.1, ease: [0.215, 0.61, 0.355, 1] }, // ease-out-cubic
              }}
              exit={{
                opacity: 0,
                y: 6,
                transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }, // ease-out-quad, no delay
              }}
            >
              <EvidenceVerbPopover
                verbId={activeVerb.id}
                verbLabel={activeVerb.label}
                verbIcon={activeVerb.icon}
                items={activeVerb.items}
                onItemClick={(starter, triggerSuffix) => handleItemClick(starter, activeVerb.id, triggerSuffix)}
                onItemHover={guardedSuggestionHover}
                onClose={handleClose}
                isMobile
              />
            </m.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Desktop: chips with absolute popover overlay
  return (
    <div ref={anchorRef} className="relative flex w-full flex-col items-center">
      {/* Chips stay mounted to preserve layout height; hidden when popover is open */}
      <div
        className={cn(
          'w-full transition-opacity duration-200',
          isPopoverOpen && 'pointer-events-none opacity-0'
        )}
      >
        {verbList}
      </div>
      {/* Popover overlays from the top of the container, expanding downward */}
      <AnimatePresence onExitComplete={handleExitComplete}>
        {isPopoverOpen && activeVerb && (
          <m.div
            ref={popoverRef}
            key={`popover-${activeVerb.id}`}
            className="absolute inset-x-0 top-0 z-10 px-5"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <EvidenceVerbPopover
              verbId={activeVerb.id}
              verbLabel={activeVerb.label}
              verbIcon={activeVerb.icon}
              items={activeVerb.items}
              onItemClick={(starter, triggerSuffix) => handleItemClick(starter, activeVerb.id, triggerSuffix)}
              onItemHover={guardedSuggestionHover}
              onClose={handleClose}
              maxHeight={popoverMaxHeight}
            />
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const EvidenceVerbCollapsedChips = ({
  onSelectVerb,
  isWorkHomeDirectionCTaxonomyEnabled = false,
}: {
  onSelectVerb: (verbId: EvidenceVerbCardId) => void;
  /** Must match the expanded card's flag, or a chip opens an L1 the card lacks. */
  isWorkHomeDirectionCTaxonomyEnabled?: boolean;
}) => {
  const allVerbs = useEvidenceHomepageVerbs(isWorkHomeDirectionCTaxonomyEnabled);
  const verbs = useMemo(
    () => allVerbs.filter((v) => v.id !== 'fill'),
    [allVerbs]
  );

  const handleVerbClick = (verbId: EvidenceVerbCardId) => {
    trackEvidenceVerbClicked(verbId, true);
    onSelectVerb(verbId);
  };

  return (
    <div
      className="w-full min-w-0"
      data-testid="work-home-evidence-starters-chips"
      data-compact-composer-interaction
    >
      {/* Free-standing under a centred composer, so it centres to the same axis. */}
      <EvidenceVerbChips
        verbs={verbs}
        onVerbClick={handleVerbClick}
        align="center"
      />
    </div>
  );
};